import { Link } from '@tanstack/react-router'
import { ChevronRight, Search } from 'lucide-react'
import { cn, formatMoney, formatNumber } from '@/lib/utils'
import { useDebouncedValue } from '@/hooks/use-debounced-value'
import { ReviewChoiceGroup } from '@/components/review-choice-group'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  type Group,
  type Measure,
  type QuotedVsActual,
  type QuotedVsActualRow,
  useQuotedVsActual,
} from './api'
import { RowSheet } from './row-sheet'
import { days, notRecordedText, rowName } from './format'
import {
  MONTH_OPTIONS,
  type QuotedVsActualSearch,
  type QuotedVsActualView,
  searchFromView,
  viewFromSearch,
} from './search-state'
import { DaysGap, MoneyGap, SourceLabel } from './shared'
import { SearchInput } from '@/components/search-input'

const PAGE_SIZE = 50

const MEASURE_OPTIONS = [
  { value: 'lead_time', label: 'Lead time' },
  { value: 'net', label: 'Net' },
  { value: 'freight', label: 'Freight' },
] as const

const GROUP_OPTIONS = [
  { value: 'manufacturer', label: 'Manufacturers' },
  { value: 'category', label: 'Categories' },
  { value: 'product', label: 'Products' },
] as const

const GROUP_NOUN: Record<Group, string> = {
  manufacturer: 'Manufacturer',
  category: 'Category',
  product: 'Product',
}

export function QuotedVsActualPage({
  searchParams,
  onSearchParamsChange,
}: {
  searchParams: QuotedVsActualSearch
  /** ``replace`` for typing and the drawer, so Back skips them. */
  onSearchParamsChange: (next: QuotedVsActualSearch, options: { replace: boolean }) => void
}) {
  const view = viewFromSearch(searchParams)
  const { measure, group, months, search, page, openKey } = view
  const change = (patch: Partial<QuotedVsActualView>, replace = false) =>
    onSearchParamsChange(searchFromView({ ...view, ...patch }), { replace })
  const debouncedSearch = useDebouncedValue(search, 250)
  const query = useQuotedVsActual({
    group,
    measure,
    months,
    search: debouncedSearch,
    page,
    pageSize: PAGE_SIZE,
  })
  const data = query.data
  const openRow = data?.rows.find((row) => row.key === openKey) ?? null

  return (
    <div className="mx-auto max-w-7xl space-y-4 pb-24">
      <header className="flex flex-wrap items-center justify-between gap-3">
        <h1 className="text-2xl font-bold tracking-tight">Quoted vs actual</h1>
        <Select
          value={String(months)}
          onValueChange={(value) => change({ months: Number(value), page: 1 })}
        >
          <SelectTrigger className="w-[170px]" aria-label="POs made in">
            <SelectValue />
          </SelectTrigger>
          <SelectContent>
            {MONTH_OPTIONS.map((option) => (
              <SelectItem key={option} value={String(option)}>
                Last {option} months
              </SelectItem>
            ))}
          </SelectContent>
        </Select>
      </header>

      <div className="flex flex-wrap items-center gap-3">
        <ReviewChoiceGroup
          label="Compare"
          value={measure}
          options={MEASURE_OPTIONS}
          onChange={(value) => change({ measure: value, page: 1 })}
        />
        <ReviewChoiceGroup
          label="Group by"
          value={group}
          options={GROUP_OPTIONS}
          onChange={(value) => change({ group: value, search: '', page: 1 })}
        />
        <div className="relative min-w-[200px] flex-1 sm:max-w-xs">
          <Search
            className="pointer-events-none absolute left-2.5 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground"
            aria-hidden
          />
          <SearchInput
            type="search"
            value={search}
            onChange={(event) => change({ search: event.target.value, page: 1 }, true)}
            placeholder={`Search ${GROUP_OPTIONS.find((option) => option.value === group)?.label.toLowerCase()}`}
            aria-label="Search"
            className="pl-8"
          />
        </div>
      </div>

      {data && <Summary data={data} measure={measure} />}

      {query.isLoading ? (
        <div className="space-y-2">
          {Array.from({ length: 8 }).map((_, index) => (
            <Skeleton key={index} className="h-12 w-full" />
          ))}
        </div>
      ) : query.error || !data ? (
        <p className="rounded-lg border px-4 py-6 text-sm text-muted-foreground">
          Quoted vs actual couldn’t load. Refresh the page to try again.
        </p>
      ) : data.total === 0 ? (
        <p className="rounded-lg border px-4 py-6 text-sm text-muted-foreground">
          {search ? 'Nothing matches this search.' : 'No POs sent in this period.'}
        </p>
      ) : (
        <div className={cn(query.isPlaceholderData && 'opacity-60 transition-opacity')}>
          <RowsTable rows={data.rows} group={group} measure={measure} onOpen={(key) => change({ openKey: key }, true)} />
          <Pager data={data} page={page} onPage={(next) => change({ page: next })} group={group} />
        </div>
      )}

      <RowSheet
        row={openRow}
        group={group}
        measure={measure}
        months={months}
        onClose={() => change({ openKey: null }, true)}
      />
    </div>
  )
}

function Summary({ data, measure }: { data: QuotedVsActual; measure: Measure }) {
  const totals = data.totals
  let text: string
  if (measure === 'lead_time') {
    const lead = totals.lead_time
    text =
      lead.actual_ship_days == null
        ? `${formatNumber(totals.po_count, 0)} POs · none shipped yet`
        : `${formatNumber(lead.shipped_count, 0)} shipped POs · median ${days(lead.actual_ship_days)} to ship, ${days(lead.quoted_days)} set now`
  } else {
    const money = measure === 'net' ? totals.net : totals.freight
    const actualWord = measure === 'net' ? 'invoiced' : 'actual'
    text =
      money.po_count === 0
        ? `No POs with both a quote and ${measure === 'net' ? 'a full invoice' : 'a carrier cost'} yet`
        : `${formatNumber(money.po_count, 0)} POs · ${formatMoney(money.actual)} ${actualWord} vs ${formatMoney(money.quoted)} quoted`
  }
  return <p className="text-sm tabular-nums text-muted-foreground">{text}</p>
}

function RowName({ row, group }: { row: QuotedVsActualRow; group: Group }) {
  if (group === 'product' && row.product) {
    return (
      <div className="min-w-0">
        <Link
          to="/products/$productId"
          params={{ productId: row.product.uuid }}
          className="font-medium text-primary hover:underline"
        >
          {row.product.model}
        </Link>
        <p className="truncate text-xs text-muted-foreground">
          {row.manufacturer && (
            <Link
              to="/manufacturers/$manuId"
              params={{ manuId: row.manufacturer.uuid }}
              className="hover:underline"
            >
              {row.manufacturer.name}
            </Link>
          )}
          {row.manufacturer && row.category && ' · '}
          {row.category?.name}
        </p>
      </div>
    )
  }
  if (group === 'category' && row.category) {
    const parents = row.category.path.slice(0, -1).map((step) => step.name)
    return (
      <div className="min-w-0">
        <span className="font-medium">{row.category.name}</span>
        {parents.length > 0 && (
          <p className="truncate text-xs text-muted-foreground">{parents.join(' › ')}</p>
        )}
      </div>
    )
  }
  return row.manufacturer ? (
    <Link
      to="/manufacturers/$manuId"
      params={{ manuId: row.manufacturer.uuid }}
      className="font-medium text-primary hover:underline"
    >
      {row.manufacturer.name}
    </Link>
  ) : (
    <span className="font-medium">{rowName(row)}</span>
  )
}

function OpenButton({ row, onOpen }: { row: QuotedVsActualRow; onOpen: (key: string) => void }) {
  return (
    <Button
      variant="ghost"
      size="sm"
      onClick={() => onOpen(row.key)}
      aria-label={`Open ${rowName(row)}`}
      className="tabular-nums text-muted-foreground"
    >
      {formatNumber(row.po_count, 0)} {row.po_count === 1 ? 'PO' : 'POs'}
      <ChevronRight className="h-4 w-4" />
    </Button>
  )
}

function QuotedThen({ row }: { row: QuotedVsActualRow }) {
  const note = notRecordedText(row.lead_time.not_recorded_count, row.po_count)
  return (
    <>
      <div className="tabular-nums">{days(row.lead_time.quoted_then_days)}</div>
      {note && <p className="whitespace-nowrap text-xs text-muted-foreground">{note}</p>}
    </>
  )
}

function LeadTimeCells({ row }: { row: QuotedVsActualRow }) {
  const lead = row.lead_time
  return (
    <>
      <TableCell className="text-right align-top">
        <QuotedThen row={row} />
      </TableCell>
      <TableCell className="text-right align-top">
        <div className="tabular-nums">{days(lead.quoted_days)}</div>
        <SourceLabel lead={lead} className="justify-end" />
      </TableCell>
      <TableCell className="text-right align-top tabular-nums">
        <div>{days(lead.actual_ship_days)}</div>
        {lead.shipped_count > 0 && (
          <p className="text-xs text-muted-foreground">{formatNumber(lead.shipped_count, 0)} shipped</p>
        )}
      </TableCell>
      <TableCell className="text-right align-top">
        <DaysGap gap={lead.gap_days} quoted={lead.quoted_days} />
      </TableCell>
      <TableCell className="hidden text-right align-top tabular-nums xl:table-cell">
        {days(lead.slowest_ship_days)}
      </TableCell>
      <TableCell className="hidden text-right align-top tabular-nums xl:table-cell">
        {days(lead.actual_delivery_days)}
      </TableCell>
    </>
  )
}

function MoneyCells({ row, measure }: { row: QuotedVsActualRow; measure: 'net' | 'freight' }) {
  const money = measure === 'net' ? row.net : row.freight
  if (money.po_count === 0) {
    return (
      <>
        <TableCell className="text-right text-muted-foreground">—</TableCell>
        <TableCell className="text-right text-muted-foreground">—</TableCell>
        <TableCell className="text-right text-muted-foreground">—</TableCell>
      </>
    )
  }
  return (
    <>
      <TableCell className="text-right align-top tabular-nums">{formatMoney(money.quoted)}</TableCell>
      <TableCell className="text-right align-top tabular-nums">
        <div>{formatMoney(money.actual)}</div>
        {measure === 'freight' && money.awaiting_invoice_count > 0 && (
          <p className="text-xs text-muted-foreground">
            {formatNumber(money.awaiting_invoice_count, 0)} booked, not invoiced
          </p>
        )}
      </TableCell>
      <TableCell className="text-right align-top">
        <MoneyGap money={money} />
      </TableCell>
    </>
  )
}

function RowsTable({
  rows,
  group,
  measure,
  onOpen,
}: {
  rows: QuotedVsActualRow[]
  group: Group
  measure: Measure
  onOpen: (key: string) => void
}) {
  return (
    <>
      <div className="hidden lg:block">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead>{GROUP_NOUN[group]}</TableHead>
              {measure === 'lead_time' ? (
                <>
                  <TableHead className="text-right">Quoted then</TableHead>
                  <TableHead className="text-right">Set now</TableHead>
                  <TableHead className="text-right">Actual</TableHead>
                  <TableHead className="text-right">Gap</TableHead>
                  <TableHead className="hidden text-right xl:table-cell">Slowest</TableHead>
                  <TableHead className="hidden text-right xl:table-cell">To delivery</TableHead>
                </>
              ) : (
                <>
                  <TableHead className="text-right">Quoted</TableHead>
                  <TableHead className="text-right">{measure === 'net' ? 'Invoiced' : 'Actual'}</TableHead>
                  <TableHead className="text-right">Difference</TableHead>
                </>
              )}
              <TableHead className="w-[1%]">
                <span className="sr-only">POs</span>
              </TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {rows.map((row) => (
              <TableRow key={row.key}>
                <TableCell className="max-w-[360px] whitespace-normal align-top">
                  <RowName row={row} group={group} />
                </TableCell>
                {measure === 'lead_time' ? (
                  <LeadTimeCells row={row} />
                ) : (
                  <MoneyCells row={row} measure={measure} />
                )}
                <TableCell className="text-right align-top">
                  <OpenButton row={row} onOpen={onOpen} />
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <ul className="space-y-2 lg:hidden">
        {rows.map((row) => (
          <li key={row.key} className="rounded-lg border p-3">
            <div className="flex items-start justify-between gap-3">
              <RowName row={row} group={group} />
              <OpenButton row={row} onOpen={onOpen} />
            </div>
            <MobileFigures row={row} measure={measure} />
          </li>
        ))}
      </ul>
    </>
  )
}

function MobileFigures({ row, measure }: { row: QuotedVsActualRow; measure: Measure }) {
  if (measure === 'lead_time') {
    const lead = row.lead_time
    return (
      <dl className="mt-2 grid grid-cols-4 gap-2 text-sm tabular-nums">
        <div>
          <dt className="text-xs text-muted-foreground">Quoted then</dt>
          <dd>
            <QuotedThen row={row} />
          </dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Set now</dt>
          <dd>{days(lead.quoted_days)}</dd>
          <SourceLabel lead={lead} />
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Actual</dt>
          <dd>{days(lead.actual_ship_days)}</dd>
        </div>
        <div>
          <dt className="text-xs text-muted-foreground">Gap</dt>
          <dd>
            <DaysGap gap={lead.gap_days} quoted={lead.quoted_days} />
          </dd>
        </div>
      </dl>
    )
  }
  const money = measure === 'net' ? row.net : row.freight
  return (
    <dl className="mt-2 grid grid-cols-3 gap-2 text-sm tabular-nums">
      <div>
        <dt className="text-xs text-muted-foreground">Quoted</dt>
        <dd>{money.po_count ? formatMoney(money.quoted) : '—'}</dd>
      </div>
      <div>
        <dt className="text-xs text-muted-foreground">{measure === 'net' ? 'Invoiced' : 'Actual'}</dt>
        <dd>{money.po_count ? formatMoney(money.actual) : '—'}</dd>
      </div>
      <div>
        <dt className="text-xs text-muted-foreground">Difference</dt>
        <dd>{money.po_count ? <MoneyGap money={money} /> : '—'}</dd>
      </div>
    </dl>
  )
}

function Pager({
  data,
  page,
  onPage,
  group,
}: {
  data: QuotedVsActual
  page: number
  onPage: (page: number) => void
  group: Group
}) {
  const pages = Math.max(1, Math.ceil(data.total / PAGE_SIZE))
  if (pages <= 1) return null
  const noun = group === 'manufacturer' ? 'manufacturers' : group === 'category' ? 'categories' : 'products'
  return (
    <div className="flex items-center justify-between py-3">
      <p className="text-sm tabular-nums text-muted-foreground">
        Page {page} of {pages} ({formatNumber(data.total, 0)} {noun})
      </p>
      <div className="flex gap-2">
        <Button variant="outline" size="sm" onClick={() => onPage(page - 1)} disabled={page <= 1}>
          Previous
        </Button>
        <Button variant="outline" size="sm" onClick={() => onPage(page + 1)} disabled={page >= pages}>
          Next
        </Button>
      </div>
    </div>
  )
}
